// UpdateResource.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  fetchRoles,
  fetchDepartments,
  fetchBanks,
  fetchEmployeeTypes,
  fetchPaymentGroups,
  fetchCostCenters,
  fetchOfficeCodes,
  fetchPaymentBanks,
  fetchSalaryConfig,
} from "../Api/ResourceMaster";
import { getCompanyCodesByOrganisation } from "../Api/CustomerMaster";
import { Form, Button, Row, Col, Accordion } from "react-bootstrap";
import { toast } from "react-toastify";
import Sample14 from "../Components/Logo";
import { useAuth } from "../Context/AuthContext";
import { Base_URL } from "../url";
import axios from "axios";

const UpdateResource = () => {
  const { id } = useParams();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [showSpouseName, setShowSpouseName] = useState(false);

  // dropdowns
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [banks, setBanks] = useState([]);
  const [employeeTypes, setEmployeeTypes] = useState([]);
  const [paymentGroups, setPaymentGroups] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [officeCodes, setOfficeCodes] = useState([]);
  const [paymentBanks, setPaymentBanks] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [salaryConfig, setSalaryConfig] = useState({});

  const defaultLocations = [
    { code: "Work from Home", description: "Work from Home" },
    { code: "Work from Office", description: "Work from Office" },
    { code: "Work from Client", description: "Work from Client" },
  ];

  const maritalStatusOptions = [
    { code: "Married", description: "Married" },
    { code: "Unmarried", description: "Unmarried" },
    { code: "Not declared", description: "Not declared" },
  ];

  const bloodGroupOptions = [
    { code: "A+", description: "A+" },
    { code: "A-", description: "A-" },
    { code: "B+", description: "B+" },
    { code: "B-", description: "B-" },
    { code: "O+", description: "O+" },
    { code: "O-", description: "O-" },
    { code: "AB+", description: "AB+" },
    { code: "AB-", description: "AB-" },
  ];

  // resource state
  const [resourceData, setResourceData] = useState({
    name: "",
    role: "",
    department: "",
    resourceId: "",
    bankName: "",
    accountNo: "",
    ifscCode: "",
    employeeType: "",
    paymentGroup: "",
    emailId: "",
    companyCode: 0,
    paymentBank: "",
    costCenter: "",
    defaultLocation: "",
    officeCode: "",
    dob: "",
    fathersName: "",
    spouseName: "",
    maritalStatus: "",
    phoneNumber: "",
    panNumber: "",
    aadharNumber: "",
    bloodGroup: "",
    uanNumber: "",
    urlAadharCard: "",
    urlPanCard: "",
    emergencyContactName: "",
    emergencyContactRelation: "",
    emergencyContactPhoneNumber: "",
    createdBy: user?.userId,
    organisationId: user?.organisationId,
    salary: {
      basic: "0",
      hra: "0",
      ma: "0",
      ta: "0",
      splAll: "0",
      ca: "0",
      mobile: "0",
      pf: "0",
      esic: "0",
      da: "0",
      pfCompany:"0",
      esicCompany:"0",
      gross: 0,
      other: 0,
    },
  });

  // helper to format API datetime -> yyyy-mm-dd for input[type=date]
  const formatToDateInput = (isoLike) => {
    if (!isoLike) return "";
    const d = new Date(isoLike);
    if (isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // fetch dropdowns + salaryConfig + companyCodes
  useEffect(() => {
    const fetchAllDropdowns = async () => {
      try {
        const [
          rolesData,
          deptsData,
          banksData,
          empTypesData,
          paymentGroupsData,
          costCentersData,
          officeCodesData,
          paymentBanksData,
          salaryconfig,
          companyCodeData,
        ] = await Promise.all([
          fetchRoles(),
          fetchDepartments(),
          fetchBanks(user?.organisationId),
          fetchEmployeeTypes(),
          fetchPaymentGroups(),
          fetchCostCenters(),
          fetchOfficeCodes(user?.organisationId),
          fetchPaymentBanks(user?.organisationId),
          fetchSalaryConfig(user?.organisationId),
          getCompanyCodesByOrganisation(user?.organisationId),
        ]);

        setRoles(rolesData || []);
        setDepartments(deptsData || []);
        setBanks(banksData || []);
        setEmployeeTypes(empTypesData || []);
        setPaymentGroups(paymentGroupsData || []);
        setCostCenters(costCentersData || []);
        setOfficeCodes(officeCodesData || []);
        setPaymentBanks(paymentBanksData || []);
        setSalaryConfig(salaryconfig || {});
        setCompanyCodes(companyCodeData || []);
      } catch (err) {
        toast.error(`Error loading dropdown data: ${err?.message || err}`);
      }
    };

    if (user?.organisationId) {
      fetchAllDropdowns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.organisationId]);

  // fetch resource by id and populate
  useEffect(() => {
    const getResource = async () => {
      setFetchingData(true);
      try {
        const res = await fetch(
          `${Base_URL}/Resource/GetResourceById/id?id=${encodeURIComponent(
            id
          )}`
        );
        if (!res.ok) {
          throw new Error(`Failed to fetch resource (status ${res.status})`);
        }
        const data = await res.json();

        const apiResource = data?.resource || {};
        const apiSalary = data?.salary || {};

        // map salary with backward compatibility (basicPlusDa / other)
        const basic =
          parseFloat(apiSalary.basic ?? apiSalary.basicPlusDa ?? 0) || 0;
        const hra = parseFloat(apiSalary.hra ?? 0) || 0;
        const ma = parseFloat(apiSalary.ma ?? 0) || 0;
        const ta = parseFloat(apiSalary.ta ?? 0) || 0;
        const splAll =
          parseFloat(apiSalary.splAll ?? apiSalary.other ?? 0) || 0;
        const ca = parseFloat(apiSalary.ca ?? 0) || 0;
        const mobile = parseFloat(apiSalary.mobile ?? 0) || 0;
        const pf = parseFloat(apiSalary.pfEmployee ?? 0) || 0;
        const esic = parseFloat(apiSalary.esicEmployee ?? apiSalary.esi ?? 0) || 0;
        const da = parseFloat(apiSalary.da ?? 0) || 0;
        const pfCompany = parseFloat(apiSalary.pfCompany ?? 0) || 0;
        const esicCompany = parseFloat(apiSalary.esicCompany ?? 0) || 0;
        const other = ca + mobile + da;
        const gross =
          basic + hra + ma + ta + splAll + other - (pf || 0) - (esic || 0)+pfCompany+esicCompany;

        setResourceData((prev) => ({
          ...prev,
          name: apiResource.name ?? "",
          role: apiResource.role ?? "",
          department: apiResource.department ?? "",
          resourceId: apiResource.resourceId ?? "",
          bankName: apiResource.bankName ?? "",
          accountNo: apiResource.accountNo ?? "",
          ifscCode: apiResource.ifscCode ?? "",
          employeeType: apiResource.employeeType ?? "",
          paymentGroup: apiResource.paymentGroup ?? "",
          emailId: apiResource.emailId ?? "",
          companyCode:
            apiResource.companyCode ?? prev.companyCode ?? 0,
          paymentBank: apiResource.paymentBank ?? "",
          costCenter: apiResource.costCenter ?? "",
          defaultLocation: apiResource.defaultLocation ?? "",
          officeCode: apiResource.officeCode ?? "",
          dob: formatToDateInput(apiResource.dob),
          fathersName: apiResource.fathersName ?? "",
          spouseName: apiResource.spouseName ?? "",
          maritalStatus: apiResource.maritalStatus ?? "",
          phoneNumber: apiResource.phoneNumber ?? "",
          panNumber: apiResource.panNumber ?? "",
          aadharNumber: apiResource.aadharNumber ?? "",
          bloodGroup: apiResource.bloodGroup ?? "",
          uanNumber: apiResource.uanNumber ?? "",
          urlAadharCard: apiResource.urlAadharCard ?? "",
          urlPanCard: apiResource.urlPanCard ?? "",
          emergencyContactName: apiResource.emergencyContactName ?? "",
          emergencyContactRelation:
            apiResource.emergencyContactRelation ?? "",
          emergencyContactPhoneNumber:
            apiResource.emergencyContactPhoneNumber ?? "",
          organisationId:
            apiResource.organisationId ?? user?.organisationId,
          salary: {
            basic,
            hra,
            ma,
            ta,
            splAll,
            ca,
            mobile,
            pf,
            esic,
            da,
            pfCompany,
            esicCompany,
            other: Number(other.toFixed(2)),
            gross: Number(gross.toFixed(2)),
          },
        }));

        // show spouse if maritalStatus is Married
        setShowSpouseName(
          (apiResource?.maritalStatus ?? "").toString() === "Married"
        );
      } catch (error) {
        toast.error(`Error loading resource: ${error?.message || error}`);
      } finally {
        setFetchingData(false);
      }
    };

    if (id) getResource();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.organisationId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "maritalStatus") {
      const isMarried = value === "Married";
      setShowSpouseName(isMarried);
      if (!isMarried) {
        setResourceData((prev) => ({
          ...prev,
          [name]: value,
          spouseName: "",
        }));
        return;
      }
    }

    setResourceData((prev) => ({
      ...prev,
      [name]: name === "companyCode" ? parseInt(value) || 0 : value,
    }));
  };

  const handleSalaryChange = (e) => {
    const { name, value } = e.target;
    const numeric = value === "" ? 0 : parseFloat(value);
    setResourceData((prev) => {
      const newSalary = {
        ...prev.salary,
        [name]: isNaN(numeric) ? 0 : numeric,
      };

      const other =
        (parseFloat(newSalary.ca) || 0) +
        (parseFloat(newSalary.mobile) || 0) +
        (parseFloat(newSalary.da) || 0);

      const gross =
        (parseFloat(newSalary.basic) || 0) +
        (parseFloat(newSalary.hra) || 0) +
        (parseFloat(newSalary.ma) || 0) +
        (parseFloat(newSalary.ta) || 0) +
        (parseFloat(newSalary.splAll) || 0) +
        other -
        (parseFloat(newSalary.esic) || 0) -
        (parseFloat(newSalary.pf) || 0)
        +(parseFloat(newSalary.pfCompany) || 0)
        +(parseFloat(newSalary.esicCompany) || 0);

      newSalary.other = Number(other.toFixed(2));
      newSalary.gross = Number(gross.toFixed(2));

      return {
        ...prev,
        salary: newSalary,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.put(
        `${Base_URL}/Resource/UpdateResourceMasters`,
        resourceData
      );
      if (res.data != null) {
        toast.success(`${res?.data} ${resourceData.name} Updated Successfully`);
      }
    } catch (err) {
      toast.error(`Error updating resource: ${err?.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fields-container">
      <h2 className="fields-title">Update Resource</h2>

      <Form onSubmit={handleSubmit} className="fields-form">
        <Accordion defaultActiveKey="0" alwaysOpen>
          {/* Employee Details */}
          <Accordion.Item eventKey="0">
            <Accordion.Header>Employee Details</Accordion.Header>
            <Accordion.Body>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Name <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={resourceData.name}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Date of Birth <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="dob"
                      value={resourceData.dob}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Role <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Select
                      name="role"
                      value={resourceData.role}
                      onChange={handleChange}
                      required
                      disabled={fetchingData}
                    >
                      <option value="">Select Role</option>
                      {roles.map((role) => (
                        <option key={role.code} value={role.code}>
                          {role.code} - {role.description}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Department <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Select
                      name="department"
                      value={resourceData.department}
                      onChange={handleChange}
                      required
                      disabled={fetchingData}
                    >
                      <option value="">Select Department</option>
                      {departments.map((dept) => (
                        <option key={dept.code} value={dept.code}>
                          {dept.code} - {dept.description}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Resource ID/Employee ID{" "}
                      <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="resourceId"
                      value={resourceData.resourceId}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Employee Type <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Select
                      name="employeeType"
                      value={resourceData.employeeType}
                      onChange={handleChange}
                      required
                      disabled={fetchingData}
                    >
                      <option value="">Select Employee Type</option>
                      {employeeTypes.map((type) => (
                        <option key={type.code} value={type.code}>
                          {type.code} - {type.description}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Email ID <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="emailId"
                      value={resourceData.emailId}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Company Code <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Select
                      name="companyCode"
                      value={resourceData.companyCode}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Company</option>
                      {companyCodes.map((company) => (
                        <option
                          key={company.companyCode}
                          value={company.companyCode}
                        >
                          {company.companyCode} - {company.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

              
              </Row>

              <Row>
               

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Office Code <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Select
                      name="officeCode"
                      value={resourceData.officeCode}
                      onChange={handleChange}
                      required
                      disabled={fetchingData}
                    >
                      <option value="">Select Office Code</option>
                      {officeCodes.map((office) => (
                        <option key={office.code} value={office.code}>
                          {office.code} - {office.description}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>

          {/* Personal Details */}
          <Accordion.Item eventKey="1">
            <Accordion.Header>Personal Details</Accordion.Header>
            <Accordion.Body>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Father's Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="fathersName"
                      value={resourceData.fathersName}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Marital Status</Form.Label>
                    <Form.Select
                      name="maritalStatus"
                      value={resourceData.maritalStatus}
                      onChange={handleChange}
                    >
                      <option value="">Select Marital Status</option>
                      {maritalStatusOptions.map((status) => (
                        <option key={status.code} value={status.code}>
                          {status.description}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                {showSpouseName && (
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Spouse Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="spouseName"
                        value={resourceData.spouseName}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                )}
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="phoneNumber"
                      value={resourceData.phoneNumber}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>PAN Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="panNumber"
                      value={resourceData.panNumber}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Aadhar Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="aadharNumber"
                      value={resourceData.aadharNumber}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Blood Group</Form.Label>
                    <Form.Select
                      name="bloodGroup"
                      value={resourceData.bloodGroup}
                      onChange={handleChange}
                    >
                      <option value="">Select Blood Group</option>
                      {bloodGroupOptions.map((group) => (
                        <option key={group.code} value={group.code}>
                          {group.description}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>UAN Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="uanNumber"
                      value={resourceData.uanNumber}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>

          {/* Bank Details */}
          <Accordion.Item eventKey="2">
            <Accordion.Header>Bank Details</Accordion.Header>
            <Accordion.Body>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Bank Name</Form.Label>
                    <Form.Select
                      name="bankName"
                      value={resourceData.bankName}
                      onChange={handleChange}
                      disabled={fetchingData}
                    >
                      <option value="">Select Bank</option>
                      {banks.map((bank) => (
                        <option key={bank.code} value={bank.code}>
                          {bank.code} - {bank.description}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Account No</Form.Label>
                    <Form.Control
                      type="text"
                      name="accountNo"
                      value={resourceData.accountNo}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>IFSC Code</Form.Label>
                    <Form.Control
                      type="text"
                      name="ifscCode"
                      value={resourceData.ifscCode}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Payment Bank</Form.Label>
                    <Form.Select
                      name="paymentBank"
                      value={resourceData.paymentBank}
                      onChange={handleChange}
                      disabled={fetchingData}
                    >
                      <option value="">Select Payment Bank</option>
                      {paymentBanks.map((bank, index) => (
                        <option key={index} value={bank.code}>
                          {bank.description}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Payment Group</Form.Label>
                    <Form.Select
                      name="paymentGroup"
                      value={resourceData.paymentGroup}
                      onChange={handleChange}
                      disabled={fetchingData}
                    >
                      <option value="">Select Payment Group</option>
                      {paymentGroups.map((group) => (
                        <option key={group.code} value={group.code}>
                          {group.code} - {group.description}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>

          {/* Other Details */}
          <Accordion.Item eventKey="3">
            <Accordion.Header>Other Details</Accordion.Header>
            <Accordion.Body>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cost Center</Form.Label>
                    <Form.Select
                      name="costCenter"
                      value={resourceData.costCenter}
                      onChange={handleChange}
                      disabled={fetchingData}
                    >
                      <option value="">Select Cost Center</option>
                      {costCenters.map((center) => (
                        <option key={center.code} value={center.code}>
                          {center.code} - {center.description}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Default Location</Form.Label>
                    <Form.Select
                      name="defaultLocation"
                      value={resourceData.defaultLocation}
                      onChange={handleChange}
                    >
                      <option value="">Select Default Location</option>
                      {defaultLocations.map((location) => (
                        <option key={location.code} value={location.code}>
                          {location.code} - {location.description}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>URL Aadhar Card</Form.Label>
                    <Form.Control
                      type="text"
                      name="urlAadharCard"
                      value={resourceData.urlAadharCard}
                      onChange={handleChange}
                      placeholder="Enter URL for Aadhar Card"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>URL PAN Card</Form.Label>
                    <Form.Control
                      type="text"
                      name="urlPanCard"
                      value={resourceData.urlPanCard}
                      onChange={handleChange}
                      placeholder="Enter URL for PAN Card"
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Emergency Contact Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="emergencyContactName"
                      value={resourceData.emergencyContactName}
                      onChange={handleChange}
                      placeholder="Enter emergency contact name"
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Emergency Contact Relation</Form.Label>
                    <Form.Control
                      type="text"
                      name="emergencyContactRelation"
                      value={resourceData.emergencyContactRelation}
                      onChange={handleChange}
                      placeholder="Enter relation (e.g., Father, Mother)"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Emergency Contact Phone</Form.Label>
                    <Form.Control
                      type="text"
                      name="emergencyContactPhoneNumber"
                      value={resourceData.emergencyContactPhoneNumber}
                      onChange={handleChange}
                      placeholder="Enter emergency contact phone"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>

          {/* Salary Details */}
          <Accordion.Item eventKey="4">
            <Accordion.Header>Salary Details</Accordion.Header>
            <Accordion.Body>
              <Row>
                {salaryConfig?.basicPay && (
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Basic</Form.Label>
                      <Form.Control
                        type="number"
                        name="basic"
                        value={resourceData.salary.basic}
                        onChange={handleSalaryChange}
                        step="0.01"
                      />
                    </Form.Group>
                  </Col>
                )}
                {salaryConfig?.hra && (
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>HRA</Form.Label>
                      <Form.Control
                        type="number"
                        name="hra"
                        value={resourceData.salary.hra}
                        onChange={handleSalaryChange}
                        step="0.01"
                      />
                    </Form.Group>
                  </Col>
                )}
                {salaryConfig?.ma && (
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>MA</Form.Label>
                      <Form.Control
                        type="number"
                        name="ma"
                        value={resourceData.salary.ma}
                        onChange={handleSalaryChange}
                        step="0.01"
                      />
                    </Form.Group>
                  </Col>
                )}
              </Row>

              <Row>
                {salaryConfig?.ta && (
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>TA</Form.Label>
                      <Form.Control
                        type="number"
                        name="ta"
                        value={resourceData.salary.ta}
                        onChange={handleSalaryChange}
                        step="0.01"
                      />
                    </Form.Group>
                  </Col>
                )}
                {salaryConfig?.da && (
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>DA</Form.Label>
                      <Form.Control
                        type="number"
                        name="da"
                        value={resourceData.salary.da}
                        onChange={handleSalaryChange}
                        step="0.01"
                      />
                    </Form.Group>
                  </Col>
                )}
                {salaryConfig?.ca && (
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>CA</Form.Label>
                      <Form.Control
                        type="number"
                        name="ca"
                        value={resourceData.salary.ca}
                        onChange={handleSalaryChange}
                        step="0.01"
                      />
                    </Form.Group>
                  </Col>
                )}
              </Row>

              <Row>
                {salaryConfig?.mobileInternetAllowence && (
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Mobile Allowance</Form.Label>
                      <Form.Control
                        type="number"
                        name="mobile"
                        value={resourceData.salary.mobile}
                        onChange={handleSalaryChange}
                        step="0.01"
                      />
                    </Form.Group>
                  </Col>
                )}
              </Row>
               <Row>
  {salaryConfig?.pf && (
    <Col md={4}>
      <Form.Group className="mb-3">
        <Form.Label>PF Company</Form.Label>
        <Form.Control
          type="number"
          name="pfCompany"
          value={resourceData.salary.pfCompany}
          onChange={handleSalaryChange}
          step="0.01"
        />
      </Form.Group>
    </Col>
  )}

  {salaryConfig?.esi && (
    <Col md={4}>
      <Form.Group className="mb-3">
        <Form.Label>ESIC Company</Form.Label>
        <Form.Control
          type="number"
          name="esicCompany"
          value={resourceData.salary.esicCompany}
          onChange={handleSalaryChange}
          step="0.01"
        />
      </Form.Group>
    </Col>
  )}
</Row>

              <Row>
                {salaryConfig?.pf && (
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>PF Employee</Form.Label>
                      <Form.Control
                        type="number"
                        name="pf"
                        value={resourceData.salary.pf}
                        onChange={handleSalaryChange}
                        step="0.01"
                      />
                    </Form.Group>
                  </Col>
                )}

                {salaryConfig?.esi && (
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>ESIC Employee</Form.Label>
                      <Form.Control
                        type="number"
                        name="esic"
                        value={resourceData.salary.esic}
                        onChange={handleSalaryChange}
                        step="0.01"
                      />
                    </Form.Group>
                  </Col>
                )}
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Spl All+</Form.Label>
                    <Form.Control
                      type="number"
                      name="splAll"
                      value={resourceData.salary.splAll}
                      onChange={handleSalaryChange}
                      step="0.01"
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Other</Form.Label>
                    <Form.Control
                      type="number"
                      name="other"
                      value={resourceData.salary.other}
                      readOnly
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Gross</Form.Label>
                    <Form.Control
                      type="number"
                      name="gross"
                      value={resourceData.salary.gross}
                      readOnly
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

        <div className="fields-btn-container" style={{ marginTop: 16 }}>
          <Button
            variant="primary"
            type="submit"
            className="fields-submit"
            disabled={loading || fetchingData}
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </Form>

      {(loading || fetchingData) && (
        <div className="loading-overlay">
          <Sample14 />
        </div>
      )}
    </div>
  );
};

export default UpdateResource;
